import { registerSW } from 'virtual:pwa-register';
import { inject, customRef } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { crossPlatformState } from '/app/utils/app';
import { shouldAutoReloadOnRefresh } from '/app/utils/app-update';
import { config } from '/app/config';

export const useApp = () => {
    const app = inject('app');

    return app;
};

export const useReaderMode = () => {
    const enabled = useRouteQuery('reader-mode', false, {
        transform: (v) => v === 'true',
    });

    return {
        enabled,
    };
}

let updateFn = null;
let reloadPageFn = null;
let onNeedRefreshPrompt = null;
let pendingRefreshPrompt = false;
let swRegistered = false;

export function registerAppUpdateRefreshPrompt(handler) {
    onNeedRefreshPrompt = handler;

    if (pendingRefreshPrompt) {
        handler(reloadPageFn);
        pendingRefreshPrompt = false;
    }
}

function ensureServiceWorkerRegistered() {
    if (swRegistered) {
        return;
    }

    swRegistered = true;

    reloadPageFn = registerSW({
        immediate: true,
        onNeedRefresh() {
            const path = window.location?.pathname || '';

            // Auto-reload only when enabled and not on a protected long-form page.
            if (shouldAutoReloadOnRefresh(path, { autoUpdate: config.app.autoUpdate })) {
                reloadPageFn?.(true);
                return;
            }

            // Otherwise surface a manual refresh prompt (also covers excluded pages).
            if (onNeedRefreshPrompt) {
                onNeedRefreshPrompt(reloadPageFn);
                return;
            }

            pendingRefreshPrompt = true;
        },
        onRegisteredSW(swUrl, r) {
            updateFn = r ? async () => {
                try {
                    if (!r?.active) return;

                    const resp = await fetch(swUrl, {
                        cache: 'no-store',
                        headers: {
                            'cache': 'no-store',
                            'cache-control': 'no-cache',
                        }
                    });

                    if (resp.status === 200) {
                        await r.update();
                    }
                } catch (error) {
                    console.warn('[app] Service worker update failed:', error);
                }
            } : null;
        },
    });
}

export const useAutoUpdate = () => {
    ensureServiceWorkerRegistered();

    return {
        tryUpdate() {
            if (config.app.autoUpdate && updateFn) {
                console.log('[app] Auto update triggered');

                updateFn();
            }
        },
    };
}

export const useCrossPlatformState = (key) => {
    const state = crossPlatformState(key);

    return (target, { transform } = {}) => {
        let transformGet = (value) => value
        let transformSet = (value) => value
      
        if (typeof transform === 'function') {
          transformGet = transform
        } else if (transform) {
          if (transform.get)
            transformGet = transform.get
          if (transform.set)
            transformSet = transform.set
        }

        return customRef((track, trigger) => {
            useEventListener(window, 'focus', trigger);

            return {
                get() {
                    track();
    
                    const value = state[target];
                    return transformGet(value);
                },
                set(value) {
                    state[target] = transformSet(value);
                    trigger();
                },
            };
        });
    }
}
